import QuantitySelect from '@/components/QuantitySelect'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { MenuItemOptionType } from '@/generated/gql/graphql'
import { numberFormat } from '@/helpers/numbers'
import { DietaryRow } from '@fusion/dietary/row'
import {
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'

import React, { useState } from 'react'
import { match } from 'ts-pattern'

import AllergensInfo from '../shared/AllergensInfo'

const canteenMenuItemOverviewItemDataFragment = graphql(`
  fragment CanteenMenuItemOverview_ItemDataFragment on MenuItem {
    _id
    name
    description
    dietaries
    allergens
    isCombo
    subAllergens {
      cereals
      nuts
    }
    variations {
      _id
      priceCustomerFacing
      isCombo
    }
    options {
      _id
      categoryType
      position
      minQty
      maxQty
      qtyPerPerson
      optionType
      name
      items {
        position
        _id
        price
        item {
          name
          dietaries
          allergens
          subAllergens {
            cereals
            nuts
          }
        }
      }
    }
    translations {
      key
      language
      value
    }
  }
`)

type Props = {
  ccVariationId: string
  itemData: FragmentType<typeof canteenMenuItemOverviewItemDataFragment>
  selectedOptions: Record<string, Record<string, { qty: number; price: number; name: string }>>
  onOptionSelection: (
    optionData: {
      categoryId: string
      optionId: string
      price: number
      name: string
    },
    optionQuantity: number,
  ) => void
  hidePrices: boolean
}

const MenuItemOverview: React.FC<Props> = ({
  ccVariationId,
  itemData: itemDataProp,
  selectedOptions,
  onOptionSelection,
  hidePrices,
}) => {
  const { allergens, dietaries, description, subAllergens, variations, options, isCombo } =
    useFragment(canteenMenuItemOverviewItemDataFragment, itemDataProp)
  const theme = useTheme()
  const isLargerDevice = useMediaQuery(theme.breakpoints.up('sm'))
  const [showFullDescription, setShowFullDescription] = useState(false)

  const ccVariation = variations?.find((variation) => variation._id === ccVariationId)
  if (!ccVariation) throw new Error('Something wrong with this item')

  const truncateLength = isLargerDevice ? 250 : 100
  const itemDescription = description || ''
  const descriptionCanBeTruncated = itemDescription.length >= truncateLength
  const descriptionToShow = showFullDescription
    ? itemDescription
    : itemDescription.slice(0, truncateLength)

  return (
    <Stack>
      <CardContent>
        <Stack direction="column" spacing={2}>
          <Typography variant="body2" whiteSpace="pre-wrap">
            {descriptionToShow}
            {!showFullDescription && descriptionCanBeTruncated ? <>&nbsp;&hellip;</> : null}
            {descriptionCanBeTruncated ? (
              <span>
                &nbsp;
                <Chip
                  sx={{ transform: 'scale(0.8)' }}
                  size="small"
                  label={showFullDescription ? 'Show less' : 'Show more'}
                  clickable
                  onClick={() => setShowFullDescription((prev) => !prev)}
                />
              </span>
            ) : null}
          </Typography>
          <AllergensInfo />
          <DietaryRow
            dietaries={dietaries || []}
            allergens={allergens || []}
            subAllergens={subAllergens}
            isComboItem={Boolean(isCombo)}
          />
          <Stack direction="column">
            {options?.map((option) => {
              const hasMinQuantity = typeof option?.minQty === 'number' && option.minQty
              const hasMaxQuantity = typeof option?.maxQty === 'number' && option.maxQty > 0
              return (
                <Stack direction="column" key={option?._id}>
                  <Divider />
                  <Stack
                    padding={1}
                    bgcolor="background.default"
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center">
                    <Typography padding={1} variant="subtitle3">
                      {option?.name}
                    </Typography>
                    <Typography color="info.main" variant="body2">
                      {hasMinQuantity && !hasMaxQuantity
                        ? `Select at least ${option?.minQty}`
                        : null}
                      {hasMaxQuantity && !hasMinQuantity ? `Select up to ${option?.maxQty}` : null}
                      {hasMaxQuantity && hasMinQuantity
                        ? `Select at least ${option.minQty} (maximum ${option.maxQty})`
                        : null}
                    </Typography>
                  </Stack>
                  <Divider />
                  {option?.items?.map((optionItem) => {
                    const optionItemId = optionItem?._id
                    if (!optionItemId) return null
                    const optionQuantity = selectedOptions[option._id]?.[optionItem._id]?.qty || 0
                    const numberOfSelectedOptionsInCategory = Object.values(
                      selectedOptions[option._id] || {},
                    ).reduce((total, value) => total + value.qty, 0)
                    return (
                      <Stack
                        paddingX={1}
                        alignItems="center"
                        justifyContent="space-between"
                        direction="row"
                        key={optionItem._id}>
                        {match(option.optionType)
                          .with(MenuItemOptionType.Ticks, () => (
                            <FormControlLabel
                              label={optionItem?.item?.name}
                              control={
                                <Checkbox
                                  disabled={Boolean(
                                    !optionQuantity &&
                                    option.maxQty &&
                                    numberOfSelectedOptionsInCategory === option.maxQty,
                                  )}
                                  onChange={(e) =>
                                    onOptionSelection(
                                      {
                                        categoryId: option._id,
                                        optionId: optionItemId,
                                        price: optionItem.price || 0,
                                        name: optionItem.item?.name || '',
                                      },
                                      e.target.checked ? 1 : 0,
                                    )
                                  }
                                  checked={optionQuantity > 0}
                                />
                              }
                              checked={optionQuantity > 0}
                            />
                          ))
                          .with(MenuItemOptionType.Numbers, () => (
                            <QuantitySelect
                              decreaseDataCy="cart-item-decrease"
                              increaseDataCy="cart-item-increase"
                              max={option.maxQty || undefined}
                              quantity={optionQuantity}
                              size="small"
                              onChange={(newValue) =>
                                onOptionSelection(
                                  {
                                    categoryId: option._id,
                                    optionId: optionItemId,
                                    price: optionItem.price || 0,
                                    name: optionItem.item?.name || '',
                                  },
                                  newValue,
                                )
                              }
                            />
                          ))
                          .otherwise(() => (
                            <></>
                          ))}
                        <Stack spacing={1} direction="row" alignItems="center">
                          <DietaryRow
                            maxToShow={3}
                            dietaries={optionItem?.item?.dietaries || []}
                            allergens={optionItem?.item?.allergens || []}
                            subAllergens={optionItem?.item?.subAllergens}
                          />
                          {!hidePrices ? (
                            <Typography color="text.secondary" variant="body2">
                              +{numberFormat((optionItem?.price || 0) / 100)}
                            </Typography>
                          ) : null}
                        </Stack>
                      </Stack>
                    )
                  })}
                </Stack>
              )
            })}
          </Stack>
        </Stack>
      </CardContent>
    </Stack>
  )
}

export default MenuItemOverview
